import { useCallback, useLayoutEffect, useRef, useState, type ClipboardEvent, type KeyboardEvent, type RefObject } from 'react'
import { applyFormat, linkAt, linkBlocker, linkEdit, linkTarget, pasteLink, unlink, type Edit, type Format } from '@/lib/textFormat'
import { caretOffset } from '@/lib/caret'
import { coarsePointer } from '@/lib/mobile'

/** На маке сочетания с ⌘: Ctrl+B там — «на букву назад», его трогать нельзя. */
export const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform)

const SHORTCUTS: Record<string, Format> = { b: 'bold', i: 'italic', u: 'underline' }

export interface Selection {
  start: number
  end: number
}

/** Что нужно плашке: выделение и что делают её кнопки. */
export interface ToolbarState {
  range: Selection
  /** Выделение ждёт адреса: вместо кнопок — поле для ссылки. */
  linking: boolean
  onFormat: (format: Format) => void
  /** Кнопка «Ссылка»: снимает ссылку или открывает поле для адреса. */
  onLink: () => void
  /** false — это не адрес сайта, поле остаётся. */
  onLinkSubmit: (raw: string) => boolean
  /** back — Esc: назад к кнопкам и к выделению в поле ввода; иначе плашка закрывается. */
  onLinkCancel: (back: boolean) => void
}

interface Options {
  /** Сама плашка — её ставим над выделением. */
  toolbarRef: RefObject<HTMLDivElement | null>
  /** Над полем уже открыта подсказка упоминаний — плашка ей не мешает. */
  hidden?: boolean
  /** После правки: чат закрывает подсказку упоминаний — правка могла оставить курсор за «#слово». */
  onApply?: () => void
}

/**
 * Панель форматирования над выделением в поле ввода и Ctrl/⌘+B, I, U.
 *
 * Панель видна, пока в поле выделен текст, и прячется, когда выделение схлопнулось, поле
 * потеряло фокус или нажали Esc. На сенсорных экранах её нет: там над выделением своё
 * системное меню. Ссылку там делают вставкой адреса поверх выделенного текста — это
 * работает везде.
 *
 * Для ссылки панель спрашивает адрес в своём поле. Фокус уходит туда, а выделение в поле
 * ввода запоминаем: панель остаётся над ним, пока адрес не введут или не передумают.
 */
export function useFormatToolbar(textareaRef: RefObject<HTMLTextAreaElement | null>, { toolbarRef, hidden = false, onApply }: Options) {
  const [range, setRange] = useState<Selection | null>(null)
  const open = range !== null && !hidden
  const [linking, setLinkingState] = useState(false)
  // обработчики потери фокуса срабатывают раньше, чем React перерисует: им нужно значение сразу
  const linkingRef = useRef(false)
  const setLinking = useCallback((on: boolean) => {
    linkingRef.current = on
    setLinkingState(on)
  }, [])
  // текст под ссылкой: если к вводу адреса поле ввода поменялось, ссылку не ставим
  const labelRef = useRef('')

  const sync = useCallback(() => {
    const el = textareaRef.current
    if (linkingRef.current) return
    if (!el || coarsePointer || document.activeElement !== el || el.selectionStart === el.selectionEnd) {
      setRange(null)
      return
    }
    const { selectionStart: start, selectionEnd: end } = el
    setRange((cur) => (cur && cur.start === start && cur.end === end ? cur : { start, end }))
  }, [textareaRef])

  const apply = useCallback(
    (format: Format) => {
      const el = textareaRef.current
      if (!el || el.disabled) return
      replace(el, applyFormat(el.value, el.selectionStart, el.selectionEnd, format))
      onApply?.()
      sync()
    },
    [textareaRef, onApply, sync],
  )

  const toggleLink = useCallback(() => {
    const el = textareaRef.current
    if (!el || el.disabled) return
    const { value, selectionStart: start, selectionEnd: end } = el
    const link = linkAt(value, start, end)
    if (link) {
      replace(el, unlink(value, link))
      onApply?.()
      sync()
      return
    }
    if (linkBlocker(value, start, end)) return
    labelRef.current = value.slice(start, end)
    setRange({ start, end })
    setLinking(true)
  }, [textareaRef, onApply, sync, setLinking])

  const cancelLink = useCallback(
    (back: boolean) => {
      if (!linkingRef.current) return
      setLinking(false)
      const el = textareaRef.current
      if (!back || !el || !range) {
        setRange(null)
        return
      }
      el.focus()
      el.setSelectionRange(range.start, range.end)
    },
    [textareaRef, range, setLinking],
  )

  const submitLink = useCallback(
    (raw: string): boolean => {
      const url = linkTarget(raw)
      if (!url) return false
      const el = textareaRef.current
      const edit = el && range && el.value.slice(range.start, range.end) === labelRef.current ? linkEdit(el.value, range.start, range.end, url) : null
      setLinking(false)
      if (!el || !edit) {
        setRange(null)
        el?.focus()
        return true
      }
      replace(el, edit)
      onApply?.()
      sync()
      return true
    },
    [textareaRef, range, onApply, sync, setLinking],
  )

  /** Адрес поверх выделенного текста — ссылка. true — вставку забрали, дальше её не передавать. */
  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>): boolean => {
    const el = e.currentTarget
    if (el.disabled) return false
    const edit = pasteLink(el.value, el.selectionStart, el.selectionEnd, e.clipboardData.getData('text/plain'))
    if (!edit) return false
    e.preventDefault()
    replace(el, edit)
    onApply?.()
    sync()
    return true
  }

  /** true — клавишу забрала панель, дальше её не передавать. */
  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): boolean => {
    if (e.key === 'Escape' && open) {
      e.preventDefault()
      setRange(null)
      return true
    }
    if (!(isMac ? e.metaKey && !e.ctrlKey : e.ctrlKey && !e.metaKey) || e.altKey || e.shiftKey) return false
    // на русской раскладке e.key — «и», «ш», «г»: тогда смотрим на саму клавишу
    const letter = /^[a-z]$/i.test(e.key) ? e.key.toLowerCase() : e.code.startsWith('Key') ? e.code.slice(3).toLowerCase() : ''
    const format = SHORTCUTS[letter]
    if (!format) return false
    // в браузерах это закладки, сведения о странице и исходный код — здесь они не нужны
    e.preventDefault()
    e.stopPropagation()
    apply(format)
    return true
  }

  // Панель встаёт над строкой, где начинается выделение, и не выходит за края поля ввода.
  // Поле растёт от правки, прокручивается, меняется окно — пересчитываем.
  useLayoutEffect(() => {
    const el = textareaRef.current
    const bar = toolbarRef.current
    if (!range || hidden || !el || !bar) return
    const place = () => position(el, bar, range.start)
    place()
    // плашка меняет ширину, когда вместо кнопок встаёт поле для ссылки
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(place)
    observer?.observe(el)
    observer?.observe(bar)
    el.addEventListener('scroll', place)
    window.addEventListener('resize', place)
    return () => {
      observer?.disconnect()
      el.removeEventListener('scroll', place)
      window.removeEventListener('resize', place)
    }
  }, [range, hidden, textareaRef, toolbarRef])

  // фокус ушёл в поле для ссылки — плашку не прячем
  const hide = useCallback(() => {
    if (!linkingRef.current) setRange(null)
  }, [])
  const toolbar: ToolbarState | null =
    open && range ? { range, linking, onFormat: apply, onLink: toggleLink, onLinkSubmit: submitLink, onLinkCancel: cancelLink } : null
  return { toolbar, onSelect: sync, onBlur: hide, onKeyDown, onPaste }
}

/**
 * Правка через execCommand: так она попадает в историю, и Ctrl+Z её отменяет. Поле получает
 * обычное событие input, и React обновляет черновик как при наборе. Где execCommand в полях
 * нет, меняем значение напрямую — без отмены, но с тем же событием.
 */
function replace(el: HTMLTextAreaElement, edit: Edit) {
  el.focus()
  el.setSelectionRange(edit.from, edit.to)
  let done = false
  try {
    done = edit.insert ? document.execCommand('insertText', false, edit.insert) : document.execCommand('delete', false)
  } catch {
    done = false
  }
  if (!done) {
    const value = el.value.slice(0, edit.from) + edit.insert + el.value.slice(edit.to)
    Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value')?.set?.call(el, value)
    el.dispatchEvent(new Event('input', { bubbles: true }))
  }
  el.setSelectionRange(edit.start, edit.end)
}

/** Координаты панели внутри обёртки поля ввода (её offsetParent). */
function position(el: HTMLTextAreaElement, bar: HTMLDivElement, at: number) {
  const wrap = bar.offsetParent
  if (!wrap) return
  const box = wrap.getBoundingClientRect()
  const field = el.getBoundingClientRect()
  const form = (el.closest('form') ?? el).getBoundingClientRect()
  const style = getComputedStyle(el)
  const padTop = parseFloat(style.paddingTop)
  const caret = caretOffset(el, at)
  // строка начала выделения; уехала за край при прокрутке — берём ближайшую видимую
  const lineTop = Math.min(Math.max(caret.top - el.scrollTop, padTop), el.clientHeight - parseFloat(style.paddingBottom) - caret.height)
  // первая строка — панель над полем, дальше — над своей строкой, поверх верхних
  const top = field.top - box.top + Math.max(lineTop - padTop, 0) - bar.offsetHeight - 6
  const left = field.left - box.left + caret.left - el.scrollLeft - 8
  const min = form.left - box.left
  const max = form.right - box.left - bar.offsetWidth
  bar.style.top = `${top}px`
  bar.style.left = `${Math.max(min, Math.min(left, max))}px`
}
